// src/pages/AdminUsersPage.js

import React, { useEffect, useState } from 'react'
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  updateDoc,
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore'
import { initializeApp, deleteApp } from 'firebase/app'
import {
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  signOut,
} from 'firebase/auth'
import { db, firebaseConfig } from '../firebase'
import {
  Container,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useAuth } from '../contexts/AuthContext'
import { SURVEY_OPTIONS } from '../config/surveys'

// Çoklu dil
import { useTranslation } from 'react-i18next'

// TabPanel bileşeni: Her sekmede gösterilecek içeriği sarmak için
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

function SurveyAccessSelect({ value, onChange, label, surveyLabels, fullWidth = false }) {
  const selectedCodes = Array.isArray(value) ? value : []

  return (
    <FormControl size="small" fullWidth={fullWidth} sx={fullWidth ? undefined : { minWidth: 260 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        label={label}
        value={selectedCodes}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(selected) => selected
          .map((code) => surveyLabels[SURVEY_OPTIONS.find((survey) => survey.code === code)?.labelKey] || code)
          .join(', ')}
      >
        {SURVEY_OPTIONS.map((survey) => (
          <MenuItem key={survey.code} value={survey.code}>
            {surveyLabels[survey.labelKey] || survey.code}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default function AdminUsersPage() {
  const { t } = useTranslation()
  
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [searchTerm, setSearchTerm] = useState('') // Arama metni
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    nickname: '',
    role: 'user',
    status: 'active',
    reportSurveyCodes: [],
  })
  const [creatingUser, setCreatingUser] = useState(false)

  // Sekme durumu
  const [tabValue, setTabValue] = useState(0)

  // Admin kontrolü
  const [isAdmin, setIsAdmin] = useState(false)

  // Auth
  const { currentUser, userRole } = useAuth()
  const surveyLabels = t('surveyReportPage.surveyOptions', { returnObjects: true })

  // Firestore'dan kullanıcıları çek
  useEffect(() => {
    const usersRef = collection(db, 'users')
    const q = query(usersRef, orderBy('createdAt', 'asc'))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      setUsers(userList)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    // Admin kontrolü
    if (userRole === 'admin') {
      setIsAdmin(true)
    } else {
      setIsAdmin(false)
    }
  }, [currentUser, userRole])

  const handleCreateUser = async (event) => {
    event.preventDefault()

    if (newUser.role === 'report_viewer' && !newUser.reportSurveyCodes.length) {
      setSnackbar({
        open: true,
        message: t('adminUsers.selectSurveyError'),
        severity: 'error',
      })
      return
    }

    setCreatingUser(true)

    const secondaryApp = initializeApp(
      firebaseConfig,
      `admin-user-creation-${Date.now()}`
    )
    const secondaryAuth = getAuth(secondaryApp)
    let createdAuthUser = null

    try {
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        newUser.email.trim(),
        newUser.password
      )
      createdAuthUser = userCredential.user

      await setDoc(doc(db, 'users', createdAuthUser.uid), {
        uid: createdAuthUser.uid,
        email: newUser.email.trim(),
        avatarUrl: '',
        nickname: newUser.nickname.trim(),
        role: newUser.role,
        status: newUser.status,
        reportSurveyCodes: newUser.role === 'report_viewer'
          ? newUser.reportSurveyCodes
          : [],
        createdAt: serverTimestamp(),
      })

      await signOut(secondaryAuth)
      setNewUser({
        email: '',
        password: '',
        nickname: '',
        role: 'user',
        status: 'active',
        reportSurveyCodes: [],
      })
      setSnackbar({
        open: true,
        message: t('adminUsers.createSuccess'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Kullanıcı oluşturma hatası:', error)

      if (createdAuthUser) {
        try {
          await deleteUser(createdAuthUser)
        } catch (rollbackError) {
          console.error('Kullanıcı oluşturma geri alma hatası:', rollbackError)
        }
      }

      const messageKey = error.code === 'auth/email-already-in-use'
        ? 'adminUsers.emailInUse'
        : error.code === 'auth/weak-password'
          ? 'adminUsers.weakPassword'
          : 'adminUsers.createError'

      setSnackbar({
        open: true,
        message: t(messageKey),
        severity: 'error',
      })
    } finally {
      await deleteApp(secondaryApp)
      setCreatingUser(false)
    }
  }

  // Mevcut admin sayısını hesaplamak için yardımcı fonksiyon
  const getAdminCount = () => {
    return users.filter((u) => u.role === 'admin').length
  }

  // Rol güncelle
  const handleRoleChange = async (userId, newRole) => {
    try {
      const userToChange = users.find((u) => u.id === userId)
      if (!userToChange) return

      const adminCount = getAdminCount()

      if (
        adminCount === 1 &&
        userToChange.role === 'admin' &&
        newRole !== 'admin'
      ) {
        setSnackbar({
          open: true,
          message: t('adminUsers.adminWarning'),
          severity: 'error',
        })
        return
      }

      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, { role: newRole })

      setSnackbar({
        open: true,
        message: t('adminUsers.userRoleUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Rol güncelleme hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.userRoleError'),
        severity: 'error',
      })
    }
  }

  // Durum güncelle
  const handleStatusChange = async (userId, newStatus) => {
    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, { status: newStatus })

      setSnackbar({
        open: true,
        message: t('adminUsers.statusUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Durum güncelleme hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.statusError'),
        severity: 'error',
      })
    }
  }

  const handleReportSurveyChange = async (userId, reportSurveyCodes) => {
    try {
      await updateDoc(doc(db, 'users', userId), { reportSurveyCodes })
      setSnackbar({
        open: true,
        message: t('adminUsers.reportAccessUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Rapor erişimi güncelleme hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.reportAccessError'),
        severity: 'error',
      })
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Kullanıcı arama
  const filteredUsers = users.filter((user) => {
    const text = searchTerm.toLowerCase()
    const emailMatch = user.email?.toLowerCase().includes(text)
    const nicknameMatch = user.nickname?.toLowerCase().includes(text)
    const firstNameMatch = user.firstName?.toLowerCase().includes(text)
    const lastNameMatch = user.lastName?.toLowerCase().includes(text)
    return emailMatch || nicknameMatch || firstNameMatch || lastNameMatch
  })

  // Aktif ve pasif kullanıcıları ayır
  const activeUsers = filteredUsers.filter((user) => user.status === 'active')
  const passiveUsers = filteredUsers.filter((user) => user.status === 'passive')

  // Sekme değişimi
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>{t('adminUsers.loading')}</Typography>
      </Container>
    )
  }

  if (!isAdmin) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{t('adminUsers.noAccess')}</Alert>
      </Container>
    )
  }

  return (
    <Container sx={{ mt: 4, backgroundColor: '#f9f9f9', padding: 4, borderRadius: 2 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <PeopleAltIcon sx={{ mr: 1 }} />
        {t('adminUsers.pageTitle')}
      </Typography>

      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardHeader
          avatar={<PersonAddIcon color="primary" />}
          title={t('adminUsers.createTitle')}
          subheader={t('adminUsers.createDescription')}
        />
        <CardContent>
          <Box component="form" onSubmit={handleCreateUser}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label={t('adminUsers.emailLabel')}
                  value={newUser.email}
                  onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  label={t('adminUsers.nickname')}
                  value={newUser.nickname}
                  onChange={(event) => setNewUser({ ...newUser, nickname: event.target.value })}
                  autoComplete="off"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  required
                  fullWidth
                  type="password"
                  label={t('adminUsers.temporaryPassword')}
                  value={newUser.password}
                  onChange={(event) => setNewUser({ ...newUser, password: event.target.value })}
                  inputProps={{ minLength: 6 }}
                  autoComplete="new-password"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                  <Select
                    label={t('adminUsers.roleLabel')}
                    value={newUser.role}
                    onChange={(event) => setNewUser({ ...newUser, role: event.target.value })}
                  >
                    <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                    <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                    <MenuItem value="report_viewer">{t('adminUsers.reportViewer')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                  <Select
                    label={t('adminUsers.statusLabel')}
                    value={newUser.status}
                    onChange={(event) => setNewUser({ ...newUser, status: event.target.value })}
                  >
                    <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                    <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={creatingUser}
                  startIcon={creatingUser ? <CircularProgress size={18} color="inherit" /> : <PersonAddIcon />}
                  sx={{ height: '56px' }}
                >
                  {t('adminUsers.createButton')}
                </Button>
              </Grid>
              {newUser.role === 'report_viewer' && (
                <Grid item xs={12}>
                  <SurveyAccessSelect
                    fullWidth
                    label={t('adminUsers.allowedSurveys')}
                    value={newUser.reportSurveyCodes}
                    surveyLabels={surveyLabels}
                    onChange={(reportSurveyCodes) => setNewUser({ ...newUser, reportSurveyCodes })}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Arama alanı */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('adminUsers.searchLabel')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* Sekmeler (Active - Passive) */}
      <Tabs value={tabValue} onChange={handleTabChange} centered>
        <Tab label={t('adminUsers.activeTab')} />
        <Tab label={t('adminUsers.passiveTab')} />
      </Tabs>

      {/* Active Kullanıcılar TabPanel */}
      <TabPanel value={tabValue} index={0}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader title={t('adminUsers.activeTab')} />
          <CardContent>
            <List>
              {activeUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    mb: 1,
                    backgroundColor: '#f1f1f1',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    p: 1, 
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl || ''}>
                      {user.nickname?.charAt(0).toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={user.email}
                    secondary={
                      <>
                        {user.nickname && (
                          <span>{t('adminUsers.nickname')}: {user.nickname} — </span>
                        )}
                        {user.firstName && user.lastName && (
                          <span>
                            {t('adminUsers.fullName')}: {user.firstName} {user.lastName}
                          </span>
                        )}
                      </>
                    }
                    sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}
                  />

                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2
                    }}
                  >
                    <FormControl
                      size="small"
                      sx={{ minWidth: 120, width: 110 }}
                    >
                      <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                      <Select
                        label={t('adminUsers.roleLabel')}
                        value={user.role || t('adminUsers.user')}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      >
                        <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                        <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                        <MenuItem value="report_viewer">{t('adminUsers.reportViewer')}</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 120, width: 120 }}
                    >
                      <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                      <Select
                        label={t('adminUsers.statusLabel')}
                        value={user.status || t('adminUsers.active')}
                        onChange={(e) =>
                          handleStatusChange(user.id, e.target.value)
                        }
                      >
                        <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                        <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                      </Select>
                    </FormControl>
                    {user.role === 'report_viewer' && (
                      <SurveyAccessSelect
                        label={t('adminUsers.allowedSurveys')}
                        value={user.reportSurveyCodes}
                        surveyLabels={surveyLabels}
                        onChange={(reportSurveyCodes) => handleReportSurveyChange(user.id, reportSurveyCodes)}
                      />
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Passive Kullanıcılar TabPanel */}
      <TabPanel value={tabValue} index={1}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader title={t('adminUsers.passiveTab')} />
          <CardContent>
            <List>
              {passiveUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    mb: 1,
                    backgroundColor: '#f1f1f1',
                    borderRadius: 1,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl || ''}>
                      {user.nickname?.charAt(0).toUpperCase() || '?'}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.email}
                    secondary={
                      <>
                        {user.nickname && (
                          <span>{t('adminUsers.nickname')}: {user.nickname} — </span>
                        )}
                        {user.firstName && user.lastName && (
                          <span>
                            {t('adminUsers.fullName')}: {user.firstName} {user.lastName}
                          </span>
                        )}
                      </>
                    }
                    sx={{ flex: 1 }}
                  />

                  {/* Rol Seçimi */}
                  <FormControl
                    size="small"
                    sx={{ mr: 2, minWidth: 120, width: 110 }}
                  >
                    <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                    <Select
                      label={t('adminUsers.roleLabel')}
                      value={user.role || t('adminUsers.user')}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    >
                      <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                      <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                      <MenuItem value="report_viewer">{t('adminUsers.reportViewer')}</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Kullanıcı Durum Seçimi */}
                  <FormControl
                    size="small"
                    sx={{ mr: 2, minWidth: 120, width: 120 }}
                  >
                    <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                    <Select
                      label={t('adminUsers.statusLabel')}
                      value={user.status || t('adminUsers.active')}
                      onChange={(e) =>
                        handleStatusChange(user.id, e.target.value)
                      }
                    >
                      <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                      <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                    </Select>
                  </FormControl>
                  {user.role === 'report_viewer' && (
                    <SurveyAccessSelect
                      label={t('adminUsers.allowedSurveys')}
                      value={user.reportSurveyCodes}
                      surveyLabels={surveyLabels}
                      onChange={(reportSurveyCodes) => handleReportSurveyChange(user.id, reportSurveyCodes)}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
